import styles from './AutoResaleUnclaimedModalWindow.module.scss';
import Box from '@components/Box';
import cn from 'classnames';
import FormErrorMessage from '@components/FormErrorMessage';
import InfoIcon from '@assets/icons/info-grey-18.svg';
import parse from 'html-react-parser';
import PlacingOrderButtons from '@components/PlacingOrderButtons';
import PlacingOrderHeader from '@components/PlacingOrderHeader';
import Switch from '@components/new/Switch';
import TextInputContainer from '@containers/TextInputContainer';
import { ButtonType } from '@components/Button';
import { Column, Grid, Row } from '@components/Grid';
import { IAutoResaleModalWindowProps, TAutoResaleForm } from './types';
import { sendAutoResaleUnclaimedData } from '@api/apiClient';
import { Text, TextSize, TextWeight } from '@components/Text';
import { Tooltip } from '@components/new/Tooltip';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';

const AutoResaleUnclaimedModalWindow: React.FC<IAutoResaleModalWindowProps> = (props: IAutoResaleModalWindowProps): React.ReactElement => {
  const { autoResaleData, onClose, handlerRefetchData } = props;

  const [allowMultipleInPackage, setAllowMultipleInPackage] = useState<boolean>(false);
  const [priceLimitEnabled, setPriceLimitEnabled] = useState<boolean>(false);

  const { t } = useTranslation();

  const { error, isError, isLoading, mutate } = useMutation({
    mutationFn: sendAutoResaleUnclaimedData,
    onSuccess() {
      handlerRefetchData();
      onClose();
    }
  });

  const {
    clearErrors,
    control,
    getValues,
    handleSubmit,
    setError,
    setValue,
    watch
  } = useForm<TAutoResaleForm>({
    defaultValues: {
      amountRU: ''
    },
    mode: 'onChange'
  });

  const submit = (form: TAutoResaleForm): void => {
    mutate({
      allowMultipleInPackage: allowMultipleInPackage,
      enabled: priceLimitEnabled,
      priceLimit: +form.amountRU
    });
  };

  useEffect(() => {
    const {
      allowMultipleInPackage,
      enabled,
      priceLimit
    } = autoResaleData;
    setValue('amountRU', priceLimit.toString());
    setAllowMultipleInPackage(allowMultipleInPackage);
    setPriceLimitEnabled(enabled);
  }, [autoResaleData]);

  useEffect(() => {
    const amountRU = watch('amountRU');
    setValue('amountRU', amountRU.replace(/^0+/, '').replace(/[^0-9]+/g, ''));
  }, [watch('amountRU')]);

  const handlerSendAutoResaleData = () => {
    if (priceLimitEnabled) {
      if (getValues('amountRU').length) {
        handleSubmit(submit)();
      } else {
        setError('amountRU', {
          type: 'manual',
          message: t('defaultFormErrors.required')
        });
      }
    } else {
      handleSubmit(submit)();
    }
  };

  return (
      <Grid>
        <PlacingOrderHeader
            description={t('autoResaleUnclaimedModalWindow.description')}
            title={t('autoResaleUnclaimedModalWindow.title')}
        />
        <div className={cn(styles.row, styles.rowOne)}>
          <Switch
            checked={priceLimitEnabled}
            onChange={() => {
              if (priceLimitEnabled) {
                clearErrors('amountRU');
                setAllowMultipleInPackage(false);
                setPriceLimitEnabled(false);
              } else {
                setPriceLimitEnabled(true);
              }
            }}
            title={t('autoResaleUnclaimedModalWindow.check1')}
          />
          <div className={styles.amount}>
            <div className={styles.TextInputContainer}>
              <TextInputContainer
                  control={control}
                  disabled={!priceLimitEnabled}
                  name="amountRU"
                  min={0}
                  title={t('autoResaleUnclaimedModalWindow.input')}
              />
            </div>
          </div>
          <Tooltip
              action={
                <img
                    className={styles.icon}
                    alt="Info"
                    src={InfoIcon}
                />
              }
              position="top"
              text={parse(t('autoResaleUnclaimedModalWindow.tooltipText1'))}
          />
        </div>
        <div className={cn(styles.row, styles.rowTwo)}>
          <Switch
            checked={allowMultipleInPackage}
            onChange={() => priceLimitEnabled && setAllowMultipleInPackage(!allowMultipleInPackage)}
            title={t('autoResaleUnclaimedModalWindow.check2')}
          />
          <Tooltip
              action={
                <img
                    className={styles.icon}
                    alt="Info"
                    src={InfoIcon}
                />
              }
              position="top"
              text={parse(t('autoResaleUnclaimedModalWindow.tooltipText2'))}
          />
        </div>
        <Row>
          <Column phone={12}>
            <Box mb={5}>
              <div className={styles.info}>
                <Text
                    size={TextSize.S}
                    weight={TextWeight.BOLD}
                >
                  {t('autoResaleUnclaimedModalWindow.infoTitle')}
                </Text>
                <Text size={TextSize.S}>
                  {parse(t('autoResaleUnclaimedModalWindow.infoText'))}
                </Text>
              </div>
            </Box>
          </Column>
        </Row>
        <PlacingOrderButtons
            buttons={[
              {
                action: () => onClose(),
                disabled: isLoading,
                text: t('autoResaleUnclaimedModalWindow.cancel'),
                type: ButtonType.SKELETON
              }, {
                action: handlerSendAutoResaleData,
                disabled: isLoading,
                text: t('autoResaleUnclaimedModalWindow.save'),
                type: ButtonType.BLOCK
              }
            ]}
        />
        {isError && <FormErrorMessage error={error}/>}
      </Grid>
  );
};

export default AutoResaleUnclaimedModalWindow;
